// Remaining credits strip (revamp PR 7a), on Doc Pabean and on PR 6a's Doc Manifes. Same requests and gates as the
// Mantine strip it replaces; Lumen tokens and Skeleton.
import { Coins } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Skeleton } from '@/components/lumen/skeleton';
import { BILLING_ENABLED } from '@constants/features';
import { useLanguage } from '@contexts/LanguageContext';
import useResolveCreditAccount from '@hooks/credit/use-resolve-credit-account';
import useAccountBalances from '@hooks/subscription/use-account-balances';

/** Billable products shown on Doc Pabean (PIB/PEB + Manifest house). */
const DOC_PABEAN_CREDIT_PRODUCTS = [
  { code: 'ceisa_submission', label: 'PIB / PEB' },
  { code: 'nvocc_manifest_submission', label: 'Manifest' },
] as const;

/**
 * Remaining credits strip for Doc Pabean and Doc Manifes. Only renders when
 * the PPJK has credit enforcement on (`isCreditActive` from balances API).
 */
export default function DocPabeanCreditsBanner() {
  const { t } = useLanguage();
  const { accountCode, loading: resolveLoading, isPaymentActive } = useResolveCreditAccount();
  const {
    data: balances,
    loading: balancesLoading,
    error: balancesError,
    isCreditActive,
  } = useAccountBalances(isPaymentActive ? accountCode : undefined);

  // Wait for resolve + balances so we don't flash "Sisa kredit" for inactive PPJKs.
  if (resolveLoading || (Boolean(accountCode) && balancesLoading)) {
    return (
      <div className="flex flex-wrap gap-4">
        <Skeleton className="h-9 w-40 rounded-card" />
        <Skeleton className="h-9 w-40 rounded-card" />
      </div>
    );
  }

  // Gate: linked account alone is not enough — hide when credit is inactive.
  if (!isPaymentActive || !accountCode || !isCreditActive) {
    return null;
  }

  const balanceFor = (productCode: string): number | null => {
    const row = balances.find((b) => b.product?.code === productCode);
    return row ? row.totalCredits : null;
  };

  return (
    <div
      data-tour="docpabean-credits"
      className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-line bg-label/[0.03] p-2.5"
    >
      <div className="flex flex-wrap items-center gap-4">
        <span className="flex items-center gap-1.5 font-semibold text-fg text-sm">
          <Coins aria-hidden className="size-4 text-label" />
          {t('docpabean.remainingCredits')}
        </span>
        {DOC_PABEAN_CREDIT_PRODUCTS.map(({ code, label }) => {
          const balance = balanceFor(code);
          return (
            <span
              key={code}
              className="flex items-center gap-1.5 rounded-control border border-line bg-raised px-2.5 py-1"
            >
              <span className="text-fg-muted text-xs">{label}</span>
              <span className="font-bold text-fg text-sm tabular-nums">{balance !== null ? balance : '—'}</span>
            </span>
          );
        })}
        {balancesError && <span className="text-err text-xs">{t('docpabean.errLoadBalance')}</span>}
      </div>
      {BILLING_ENABLED && (
        <Link to="/subscription" className="text-label text-xs hover:underline">
          {t('docpabean.viewSubscription')}
        </Link>
      )}
    </div>
  );
}
